import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Card } from '../../Card';
import { CardLoader } from 'src/components';
import {
  ISigningRulesAndSignatories,
  TSigningCaseDetailsRouteProps,
} from 'src/models';
import { getReject } from 'src/state/Signing/Reject/selectors';
import {
  getOtherContractingParties,
  getSigningCaseData,
  getSignatures,
} from 'src/state/SigningCaseDetails/selectors';
import * as signingRulesAndSignatoriesSelectors from 'src/state/SigningRulesAndSignatories/selectors';
import { TRootState } from 'src/state/types';
import { Signatories } from './components';
import { getCardType } from './getCardType';
import { getOpenedSigningCaseListItem } from 'src/state/SigningCasesList/selectors';
import * as fromRoot from 'src/state/rootReducer';
import { Config } from 'src/state/Config';

interface Props extends TSigningCaseDetailsRouteProps, WithTranslation {
  signingCase: ReturnType<typeof getSigningCaseData>;
  signingCaseListItem: ReturnType<typeof getOpenedSigningCaseListItem>;
  isSigningRulesAndSignatoriesLoading: boolean;
  isSignatureSuccessful: ReturnType<typeof fromRoot.getIsSignatureSuccessful>;
  signatories: ReturnType<
    typeof signingRulesAndSignatoriesSelectors.getSignatoriesV2
  >;
  signatures: ReturnType<typeof getSignatures>;
  signingRules: ISigningRulesAndSignatories['signingRules'];
  otherContractingParties: ReturnType<typeof getOtherContractingParties>;
  rejected: ReturnType<typeof getReject>;
  show: boolean;
}

const SigneesCard = ({
  signingCase,
  signatories,
  signatures,
  otherContractingParties,
  signingRules,
  show,
  t,
  isSigningRulesAndSignatoriesLoading,
  isSignatureSuccessful,
  rejected,
  signingCaseListItem,
}: Props) => {
  const hasSignatoriesOrOthercontractingParties = (otherSignatories, parties) =>
    (otherSignatories && otherSignatories.length) ||
    (parties && parties.length);

  const didSignatorySignOrReject = () =>
    rejected.data || isSignatureSuccessful.data.success;

  const isLoading = () =>
    isSigningRulesAndSignatoriesLoading && didSignatorySignOrReject();

  const doSignatoriesExist = () => signatories && signatories.length;

  const getSigningCaseStatus = () =>
    signingCase.status || (signingCaseListItem && signingCaseListItem.status);

  if (!show || !signingCase) {
    return null;
  }

  const title = t('AuthorizedSignatories');
  const status = getSigningCaseStatus();
  const cardType = getCardType(status);

  if (isLoading()) {
    return (
      <Card title={title} type={cardType}>
        <CardLoader />
      </Card>
    );
  } else if (!doSignatoriesExist()) {
    return null;
  }

  if (
    hasSignatoriesOrOthercontractingParties(
      signatories,
      otherContractingParties
    )
  ) {
    return (
      <>
        <Card
          labelText={title}
          title={title}
          type={cardType}
          data-testid="signatories-card"
        >
          <Signatories
            signatories={signatories || []}
            contractingParties={otherContractingParties}
            signatures={signatures}
            status={status}
            signingRules={signingRules}
          />
        </Card>
      </>
    );
  }

  return null;
};

export const doShowSignatories = isSigningCaseReadyToSign =>
  !(Config.isSwedishSite() && isSigningCaseReadyToSign);

const mapStateToProps = (
  state: TRootState,
  props: TSigningCaseDetailsRouteProps
) => {
  const signingCase = getSigningCaseData(state, props.match.params.caseId);
  const signatures = getSignatures(state, props.match.params.caseId);
  return {
    isSignatureSuccessful: fromRoot.getIsSignatureSuccessful(state),
    isSigningRulesAndSignatoriesLoading: signingRulesAndSignatoriesSelectors.getIsLoading(
      state
    ),
    otherContractingParties: getOtherContractingParties(
      state,
      props.match.params.caseId
    ),
    rejected: getReject(state),
    signatories: signingRulesAndSignatoriesSelectors.getSignatoriesV2(
      state,
      signingCase && signingCase.status,
      signatures
    ),
    signatures: signatures,
    signingCase: signingCase,
    signingCaseListItem: getOpenedSigningCaseListItem(state),
    signingRules: signingRulesAndSignatoriesSelectors.getSigningRules(state),
    show: doShowSignatories(
      fromRoot.isSigningCaseReadyToSign(state, props.match.params.caseId)
    ),
  };
};

const ExtendedComponent = withTranslation()(
  withRouter(connect(mapStateToProps, null)(SigneesCard))
);

export { ExtendedComponent as SigneesCard };
export default SigneesCard;
